import { useState } from "react";
import type { GameDetail, HostPairingView, Message, RoundMode } from "backend/types";
import { api } from "./api";
import { Avatar, mmss, useCountdown, usePoll } from "./ui";
import { Leaderboard } from "./Leaderboard";

const LENGTHS = [30, 60, 120, 180, 240, 300];

const PAIRING_BADGE: Record<string, string> = {
  chatting: "bg-wa/15 text-wa",
  resolved: "bg-gold/25 text-amber-700",
  timedout: "bg-black/10 text-ink/50",
};

export function HostGame({ game, onLeave }: { game: GameDetail; onLeave: () => void }) {
  const { data, refresh } = usePoll(() => api.getOverview(game.id), 2500, [game.id]);
  const [mode, setMode] = useState<RoundMode>("presale");
  const [busy, setBusy] = useState(false);
  const [openPairing, setOpenPairing] = useState<HostPairingView | null>(null);

  const overview = data?.round ?? null;
  const activeRound = game.status !== "ended" && !!game.activeRoundId;
  const secondsLeft = useCountdown(overview?.status === "active" ? overview.endsAt : null);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onLeave} className="text-sm text-ink/50 hover:text-ink">← Rooms</button>
        <h1 className="font-display text-xl font-bold">{game.name}</h1>
        <span className="text-[11px] uppercase tracking-wide bg-spice/15 text-spice rounded-full px-2 py-0.5">host</span>
      </div>

      {/* controls */}
      <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 space-y-3">
        {game.status === "ended" ? (
          <div className="text-center space-y-2">
            <div className="text-ink/60 font-display font-bold">🏁 Game ended</div>
            <button
              onClick={() => { window.location.hash = `#/review/${game.id}`; }}
              className="rounded-xl bg-wa text-white px-4 py-2 text-sm font-semibold hover:bg-wa-dark"
            >
              Review full game →
            </button>
          </div>
        ) : activeRound ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold">Round {game.activeRoundIndex} live</div>
              <div className="text-sm text-ink/50 tabular-nums">⏱ {mmss(secondsLeft)} left</div>
            </div>
            <button
              disabled={busy}
              onClick={() => act(() => api.endRound(game.activeRoundId!))}
              className="rounded-xl bg-spice text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              End round
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-ink/50">Mode</span>
              {(["presale", "postsale"] as RoundMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={
                    "text-sm rounded-full px-3 py-1 border " +
                    (mode === m ? "bg-wa text-white border-wa" : "border-black/10 hover:bg-parchment-deep/50")
                  }
                >
                  {m === "presale" ? "Fresh inquiry" : "After-sale"}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink/50">Round length</span>
              <select
                value={game.roundSeconds}
                onChange={(e) => act(() => api.configGame(game.id, { roundSeconds: Number(e.target.value) }))}
                className="rounded-lg bg-parchment-deep/50 px-2 py-1 text-sm outline-none"
              >
                {LENGTHS.map((s) => (
                  <option key={s} value={s}>{mmss(s)}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-amber-700">{!game.canStart ? game.startBlockedReason : ""}</span>
              <button
                disabled={busy || !game.canStart}
                onClick={() => act(() => api.startRound(game.id, { mode }))}
                className="rounded-xl bg-wa text-white px-5 py-2 font-semibold hover:bg-wa-dark disabled:bg-black/10 disabled:text-ink/40"
              >
                ▶ Start round {game.roundCount + 1}
              </button>
            </div>
          </>
        )}
      </div>

      {/* live overview / review */}
      {overview && (
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4">
          <div className="text-xs uppercase tracking-wide text-ink/40 mb-3">
            Round {overview.index} · {overview.status === "active" ? "live" : "review"} · tap to read
          </div>
          <ul className="space-y-2">
            {overview.pairings.map((p) => (
              <li key={p.pairingId}>
                <button
                  onClick={() => setOpenPairing(p)}
                  className="w-full text-left rounded-xl border border-black/5 hover:bg-parchment-deep/30 px-3 py-2 flex items-center gap-2"
                >
                  <Avatar color={p.customer.color} size={22} />
                  <span className="text-sm truncate max-w-[5.5rem]">{p.customer.name}</span>
                  <span className="text-ink/30">↔</span>
                  <Avatar color={p.seller.color} size={22} />
                  <span className="text-sm truncate max-w-[5.5rem]">{p.seller.name}</span>
                  <span className="text-xs text-ink/40 truncate flex-1">· {p.product}</span>
                  <span className={"text-[10px] uppercase rounded-full px-2 py-0.5 " + PAIRING_BADGE[p.status]}>
                    {p.status}
                  </span>
                  <span className="text-[11px] text-ink/40 tabular-nums">{p.messageCount}💬</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* roster management (between rounds) */}
      {!activeRound && game.status !== "ended" && (
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4">
          <div className="text-xs uppercase tracking-wide text-ink/40 mb-3">
            Players ({game.players.filter((p) => !p.isHost).length})
          </div>
          <ul className="space-y-1.5">
            {game.players.filter((p) => !p.isHost).map((p) => (
              <li key={p.playerId} className="flex items-center gap-2">
                <Avatar color={p.color} size={26} />
                <span className="text-sm flex-1 truncate">{p.name}</span>
                <span className="text-xs text-ink/40 truncate max-w-[8rem]">{p.product ?? "no product yet"}</span>
                <button
                  onClick={() => act(() => api.kick(game.id, { playerId: p.playerId }))}
                  className="text-xs text-spice hover:underline"
                >
                  remove
                </button>
              </li>
            ))}
            {game.players.filter((p) => !p.isHost).length === 0 && (
              <li className="text-sm text-ink/40">Waiting for players to join…</li>
            )}
          </ul>
        </div>
      )}

      {game.roundCount > 0 && <Leaderboard gameId={game.id} />}

      {game.status !== "ended" && (
        <button
          onClick={() => {
            if (confirm("End the whole game for everyone?")) act(() => api.endGame(game.id));
          }}
          className="text-xs text-ink/40 hover:text-spice"
        >
          End game
        </button>
      )}

      {openPairing && <TranscriptModal pairing={openPairing} onClose={() => setOpenPairing(null)} />}
    </div>
  );
}

function TranscriptModal({ pairing, onClose }: { pairing: HostPairingView; onClose: () => void }) {
  const { data } = usePoll(() => api.listMessages(pairing.pairingId), 2000, [pairing.pairingId]);
  const messages: Message[] = data?.messages ?? [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6 z-50" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg max-h-[85vh] rounded-t-2xl sm:rounded-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <div className="text-sm">
            <span className="font-semibold">{pairing.customer.name}</span> ↔{" "}
            <span className="font-semibold">{pairing.seller.name}</span>
            <div className="text-xs text-ink/40">
              {pairing.product} · {pairing.customerPersonaLabel} / {pairing.sellerPersonaLabel}
            </div>
          </div>
          <button onClick={onClose} className="text-ink/40 hover:text-ink text-lg">✕</button>
        </div>
        <div className="chat-bg flex-1 overflow-y-auto p-3 space-y-1.5">
          {messages.map((m) => {
            if (m.kind === "event") {
              return (
                <div key={m.id} className="flex justify-center my-1">
                  <span className="text-[11px] text-ink/50 bg-gold/20 rounded-full px-3 py-0.5">{m.body}</span>
                </div>
              );
            }
            const fromCustomer = m.senderPlayerId === pairing.customer.id;
            return (
              <div key={m.id} className={"flex " + (fromCustomer ? "justify-start" : "justify-end")}>
                <div
                  className={
                    "max-w-[78%] rounded-2xl px-3 py-1.5 text-sm shadow-sm " +
                    (fromCustomer ? "bg-white" : "bg-bubble-out")
                  }
                >
                  <div className="text-[10px] text-ink/40 mb-0.5">
                    {fromCustomer ? pairing.customer.name : pairing.seller.name}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && <div className="text-center text-xs text-ink/40 mt-8">No messages.</div>}
        </div>
      </div>
    </div>
  );
}
